import { ActivatedRouteSnapshot, CanActivateChildFn, CanActivateFn, RouterStateSnapshot } from '@angular/router';
import { inject, Injector, runInInjectionContext } from '@angular/core';
import { AppInitializationService } from '@core/app-initialization/app-initialization.service';
import { ShellContextService } from '../../services/shell-context/shell-context.service';
import { LogoutService } from '../../services/authentication/logout.service';
import { CompanyType, CookieName } from '@nodeModules/@itero/auth-angular';
import { TimberService } from '@logging/timber.service';
import { PlatformLoginStateService } from '../../services/platform-login/platform-login-state.service';

const redirectToPlatformLoginPage = (returnUrl?: string): false => {
	const logoutService = inject(LogoutService);

	inject(ShellContextService).resetContext();

	return logoutService.logoutWithoutSessionInvalidation({ returnUrl });
};

export const appInitializationGuard: CanActivateChildFn | CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
	const service = inject(AppInitializationService);
	const platformLoginState = inject(PlatformLoginStateService);
	const timberService = inject(TimberService);
	const cookie = service.allInitializationCookie;

	// Platform login flow: skip legacy cookie checks, auth-angular already has the token.
	if (platformLoginState.isActive()) {
		return service.init(cookie);
	}

	const hasMissingCookies = cookie.missing.length > 0;
	const isNotALab = (cookie.businessCookie.companyType as CompanyType) !== CompanyType.Lab;

	if (hasMissingCookies || isNotALab) {
		// Allow navigation if auth cookies exist but only business/infra cookies are missing.
		const authCookiesMissing = cookie.missing.some(
			m => m === CookieName.ITEROTOKEN || m === CookieName.SESSIONID || m === CookieName.IDTOKEN || m === CookieName.IDENTITYPROVIDER
		);

		if (!authCookiesMissing) {
			return service.init(cookie);
		}

		const missing = JSON.stringify(cookie.missing);
		const companyType = String(cookie.businessCookie.companyType ?? 'null');

		timberService.error(`AppInitializationGuard blocking navigation. Missing: ${missing}, companyType: ${companyType}`, {
			module: 'AppInitializationGuard',
		});

		return runInInjectionContext(inject(Injector), () => {
			return redirectToPlatformLoginPage(state.url);
		});
	}

	// All cookies present (legacy / Logon As) — clear any stale platform login state.
	platformLoginState.clear();

	return service.init(cookie);
};
